/**
 * Extract a human-readable error string from any axios / fetch error.
 *
 * FastAPI returns 4xx errors in two shapes:
 *   • Hand-written: `{ detail: "Some message" }`            → string
 *   • Pydantic v2 422: `{ detail: [{type, loc, msg, ...}] }` → array of objects
 *
 * The array case caused a hard crash when passed to `toast.error()` or
 * rendered directly into JSX ("Objects are not valid as a React child").
 * This helper normalises both shapes to a single string so callers can
 * safely do `toast.error(extractApiError(e, 'Save failed'))`.
 */
export function extractApiError(err, fallback = 'Something went wrong') {
  if (!err) return fallback;
  // axios error → err.response.data
  const data = err?.response?.data ?? err?.data ?? err;
  if (!data) return err?.message || fallback;

  const detail = data.detail ?? data.message ?? data;

  // String (the simple case)
  if (typeof detail === 'string') return detail;

  // Pydantic v2 validation error: array of {type, loc, msg, input, url}
  if (Array.isArray(detail)) {
    const msgs = detail.map((d) => {
      if (typeof d === 'string') return d;
      const field = Array.isArray(d?.loc) ? d.loc.filter((x) => x !== 'body').join('.') : '';
      const msg = d?.msg || d?.message || 'invalid value';
      return field ? `${field}: ${msg}` : msg;
    }).filter(Boolean);
    return msgs.join('; ') || fallback;
  }

  // Single object: try common message keys
  if (typeof detail === 'object') {
    return detail.msg || detail.message || detail.error || fallback;
  }

  return fallback;
}
